/**
 * ### PARTNER_MARK_BOX
 *
 * The largest a mark may be drawn on the desktop wall, in CSS pixels. The
 * mobile wall draws every mark at {@link PARTNER_MARK_MOBILE_SCALE} of this.
 */
export const PARTNER_MARK_BOX = { width: 248, height: 64 } as const;

/**
 * ### PARTNER_MARK_MOBILE_SCALE
 *
 * How much smaller the mobile wall draws each mark than the desktop one.
 */
export const PARTNER_MARK_MOBILE_SCALE = 0.8;

/**
 * Height a mark with a 1:1 ratio would get if nothing capped it. Every other
 * ratio is derived from it so that all marks cover about the same area.
 */
const SQUARE_MARK_HEIGHT = 96;

/**
 * ### PartnerMarkSize
 *
 * The desktop size one mark is drawn at, in CSS pixels.
 */
export type PartnerMarkSize = { width: number; height: number };

/**
 * ### partnerMarkSize
 *
 * Sizes a mark by its shape so every mark on the wall carries about the same
 * visual weight.
 *
 * Fitting every mark into one box makes the wide wordmarks thin strips and the
 * square marks full blocks. Holding the area constant instead — height falls
 * with the square root of the aspect ratio — keeps a wordmark and an emblem
 * reading as equals. Both dimensions are then capped to the box, so a very
 * square mark does not tower over the row and a very wide one does not run
 * into its neighbours.
 *
 * @param aspectRatio Width over height of the mark file
 * @returns The width and height to draw the mark at on desktop
 */
export function partnerMarkSize(aspectRatio: number): PartnerMarkSize {
  const height = Math.min(
    PARTNER_MARK_BOX.height,
    SQUARE_MARK_HEIGHT / Math.sqrt(aspectRatio),
    PARTNER_MARK_BOX.width / aspectRatio,
  );

  return {
    width: Math.round(height * aspectRatio),
    height: Math.round(height),
  };
}
